import React, { Component } from 'react';
import { config } from '../utils/config';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { fetchLocations } from '../utils/sheets';
import './BookFinder.css';
import Loading from './Loading';

const mapIcon = '/images/find-grumple-icon.png';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const center = {
  lat: 38.95, // Default center - moved south to avoid lake center
  lng: -120.0324
};

const mapOptions = {
  disableDefaultUI: true,
  styles: [
    { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "poi.business", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "poi.attraction", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "poi.government", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "poi.medical", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "poi.place_of_worship", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "poi.school", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "poi.sports_complex", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "transit", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] }
  ]
};

class BookFinder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      locations: [],
      selectedLocation: null,
      map: null,
      loading: true,
      showOverlay: true,
      zoom: 12,
      markerIcon: null,
      isLoaded: false
    };
    this.mapRef = React.createRef();
  }

  componentDidMount() {
    this.loadLocations();
  }

  componentDidUpdate(prevProps, prevState) {
    // Set up marker icon after Google Maps API is loaded
    if (this.state.isLoaded && !prevState.isLoaded && window.google && !this.state.markerIcon) {
      const icon = {
        url: mapIcon,
        scaledSize: new window.google.maps.Size(56, 56),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(28, 0),
        zIndex: 1000,
      };
      console.log('Setting marker icon:', icon);
      this.setState({ markerIcon: icon });
    }

    // Fade out overlay after loading is false, with 1s delay, and animate zoom
    if (!this.state.loading && prevState.loading && this.state.map) {
      setTimeout(() => {
        this.setState({ showOverlay: false });
      }, 1000);
      
      if (this.state.zoom !== 13) {
        setTimeout(() => {
          this.setState({ zoom: 13 });
          if (this.state.map) {
            this.state.map.setZoom(13);
          }
        }, 100);
      }
    }
  }

  loadLocations = async () => {
    this.setState({ loading: true });
    const data = await fetchLocations();
    console.log('Loaded locations:', data);
    console.log('First location:', data[0]);
    console.log('First location lat/lng:', data[0] ? { lat: data[0].lat, lng: data[0].lng } : 'no data');
    this.setState({ 
      locations: data,
      loading: false 
    });
  }

  onLoad = (mapInstance) => {
    this.setState({ map: mapInstance });
    this.mapRef.current = mapInstance;
  }

  onUnmount = () => {
    this.setState({ map: null });
    this.mapRef.current = null;
  }

  handleScriptLoad = () => {
    this.setState({ isLoaded: true });
    // Set up marker icon immediately when script loads
    if (window.google) {
      const icon = {
        url: mapIcon,
        scaledSize: new window.google.maps.Size(56, 56),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(28, 0),
        zIndex: 1000,
      };
      console.log('Setting marker icon on script load:', icon);
      this.setState({ markerIcon: icon });
    }
  }

  render() {
    const { locations, selectedLocation, loading, showOverlay, zoom, markerIcon, isLoaded } = this.state;

    return (
      <LoadScript
        googleMapsApiKey={config.GOOGLE_MAPS_API_KEY}
        onLoad={this.handleScriptLoad}
      >
        <div className="book-finder">
           {/* Loading overlay */}
           {showOverlay && (
            <Loading text="Finding where to buy Grumple" loading={loading}/>
          )}
         
          {/* Map */}
          <div className="map-container">
            {isLoaded && !loading && (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={zoom}
                onLoad={this.onLoad}
                onUnmount={this.onUnmount}
                options={mapOptions}
              >
                {locations.length > 0 && locations.map((location, index) => {
                  if (!location.lat || !location.lng) {
                    console.warn(`Location ${index} missing coordinates:`, location);
                    return null;
                  }
                  console.log(`Rendering marker ${index}:`, location.name, 'at', location.lat, location.lng, 'icon:', markerIcon ? 'set' : 'not set');
                  const markerProps = {
                    key: index,
                    position: { lat: parseFloat(location.lat), lng: parseFloat(location.lng) },
                    title: location.name,
                    onClick: () => this.setState({ selectedLocation: location })
                  };
                  if (markerIcon) {
                    markerProps.icon = markerIcon;
                    markerProps.zIndex = markerIcon.zIndex || 500;
                  }
                  return <Marker {...markerProps} />;
                })}
                {selectedLocation && (
                  <InfoWindow
                    position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
                    onCloseClick={() => this.setState({ selectedLocation: null })}
                  >
                    <div className="info-window-container">
                      <h3 className="info-window-title">{selectedLocation.name}</h3>
                      <p className="info-window-address-container">
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                            `${selectedLocation.address}, ${selectedLocation.city}, ${selectedLocation.state} ${selectedLocation.zip}`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="info-window-address-link"
                        >
                          {selectedLocation.address}
                          <br />
                        {selectedLocation.city}, {selectedLocation.state} {selectedLocation.zip}
                        </a>
                      </p>
                      {selectedLocation.phone && (
                        <p>
                          <b className="info-window-phone-label">Phone:</b>{' '}
                          <a
                            href={`tel:${selectedLocation.phone.replace(/[^0-9+]/g, '')}`}
                            className="info-window-phone-link"
                          >
                            {selectedLocation.phone}
                          </a>
                        </p>
                      )}
                      {selectedLocation.website && (
                        <p>
                          <a href={selectedLocation.website} target="_blank" rel="noopener noreferrer" className="info-window-website-link">
                            Website <span className="material-icons info-window-website-icon">open_in_new</span>
                          </a>
                        </p>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </div>
        </div>
      </LoadScript>
    );
  }
}

export default BookFinder;



// WEBPACK FOOTER //
// ./src/pages/findGrumple/components/BookFinder.js